import React from 'react';
import parse from 'date-fns/parse';
import { CmsCommonControls } from 'js/model/cms/cms-common-controls';
import { DateTimeSearchInput } from 'js/pages/BrowsePage/WhatWhereWhen/DateTimeSearchInput';
import { RequestData } from 'js/helpers/service';
import { SearchOptions } from 'js/model/rainbow/browse-page/SearchOptions';
import { FORMAT } from 'js/helpers/datetime-format';
import { generateDateTimeLabel } from 'js/components/controls/DateTimeInput';
import { convertHoursToString } from 'js/helpers/time';
import { CmsBrowsePage } from 'js/model/cms/cms-browse-page';
import { CmsCalendar } from 'js/model/cms/cms-calendar';
import { Aggregations } from 'js/model/rainbow/browse-page/Aggregations';
import { VenueListingSpecificationOutput } from 'js/model/rainbow/browse-page/VenueListingSpecificationOutput';
import { generateWhere } from 'js/model/view/browse-page/browse-results-summary';
import { wwwTrackingContext } from './tracking';
import { CollapsedContainer } from './CollapsedContainer';
import { LocationSearchInput } from './LocationSearchInput/LocationSearchInput';
import { TreatmentSearchInput } from './TreatmentSearchInput/TreatmentSearchInput';

interface Props {
  aggregations: Aggregations;
  cms: CmsBrowsePage;
  pageData: RequestData;
  isCollapsed: boolean;
  specification: VenueListingSpecificationOutput;
  searchOptions: SearchOptions;
  collapse: (collapsed: boolean) => void;
  treatmentName: string;
  onAnySelectFocus?: () => void;
}

export class TreatmentSearchHeader extends React.PureComponent<Props> {
  public render(): React.ReactNode {
    const {
      specification,
      cms,
      pageData,
      aggregations,
      isCollapsed,
      searchOptions,
      treatmentName,
      onAnySelectFocus,
    } = this.props;

    const cmsCommonControls: CmsCommonControls = cms.common.controls;
    const cmsCalendar: CmsCalendar = { calendar: cms.common.calendar };

    const location = specification.location!; // BrowseData ensures that location is always present.
    const locationLabelCollapsed = generateWhere(cms, specification.location);
    let dateTimeLabelCollapsed = generateDateTimeLabel(
      cmsCommonControls,
      specification.date
        ? parse(specification.date.from, FORMAT, new Date())
        : null,
      specification.startHours
        ? convertHoursToString(specification.startHours)
        : null,
      specification.endHours
        ? convertHoursToString(specification.endHours)
        : null
    );
    if (dateTimeLabelCollapsed === '') {
      dateTimeLabelCollapsed =
        cmsCommonControls['datetime-input']['date-any-date-button'];
    }

    return (
      <wwwTrackingContext.Consumer>
        {(trackingCategory) => (
          <>
            <TreatmentSearchInput
              cms={cms}
              pageData={pageData}
              aggregations={aggregations}
              treatments={specification.treatmentCategories}
              treatmentType={specification.treatmentCategoryGroup}
              venueType={specification.venueType}
              visible={!isCollapsed}
              onFocus={onAnySelectFocus}
            />
            <LocationSearchInput
              cms={cms}
              pageData={pageData}
              location={location}
              otherLocations={aggregations.locations}
              validRadius={searchOptions.validRadius}
              isErrorStyling={false}
              visible={!isCollapsed}
              trackingCategory={trackingCategory}
              onFocus={onAnySelectFocus}
            />
            <DateTimeSearchInput
              inputId="date-time-input"
              cmsCalendar={cmsCalendar}
              cmsCommonControls={cmsCommonControls}
              specification={specification}
              visible
              trackingCategory={trackingCategory}
              onFocus={onAnySelectFocus}
            />
            {isCollapsed && (
              <CollapsedContainer
                mainLabel={treatmentName}
                secondLineLabelLeft={locationLabelCollapsed}
                secondLineLabelRight={dateTimeLabelCollapsed}
                collapse={() => this.props.collapse(false)}
              />
            )}
          </>
        )}
      </wwwTrackingContext.Consumer>
    );
  }
}
